import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Eye, Trash2, PoundSterling, Columns, List, X, FileSearch } from "lucide-react";
import Shell from "@/components/Shell";
import { Card, PageHeader, Btn, Input, Spinner, Empty, StatusBadge, Select, IconBtn } from "@/components/ui-kit";
import { STATUS_DOT, STATUS_BG, formatGBP, PROJECT_STATUSES } from "@/lib/format";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";

const KANBAN_COLUMNS = ["Not Started", "In Progress", "Completed", "On Hold"];

const KanbanCard = ({ p, onDelete }) => {
  const hasProof = !!p.latest_proof;
  return (
  <Card data-testid={`project-card-${p.id}`} className={`p-4 hover:shadow-sm transition-all group ${STATUS_BG[p.status] || ""}`}>
    <Link to={`/projects/${p.id}`} className="block">
      <p className="text-sm font-semibold text-zinc-950 group-hover:text-zinc-700 transition-colors leading-snug">{p.name}</p>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-zinc-500 truncate">{p.client_name}</p>
        <p className="text-xs text-zinc-400 font-mono">#{p.job_number}</p>
      </div>
      {p.total_cost > 0 && (
        <p className="text-sm font-semibold text-zinc-950 mt-3">{formatGBP(p.total_cost)}</p>
      )}
    </Link>
    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
      <span className="text-[11px] text-zinc-500">{p.task_count} task{p.task_count !== 1 ? "s" : ""}</span>
      <div className="flex gap-1 items-center">
        <Link
          to={hasProof ? `/projects/${p.id}?proof=latest` : "#"}
          onClick={(e) => { if (!hasProof) e.preventDefault(); }}
          aria-disabled={!hasProof}
          data-testid={`project-quick-proof-${p.id}`}
          title={hasProof ? `Quick Proof View: ${p.latest_proof.name}` : "No proofs yet"}
          className={`p-1.5 rounded ${hasProof ? "text-purple-700 hover:bg-purple-100" : "text-zinc-300 cursor-not-allowed"}`}
        >
          <FileSearch className="w-3.5 h-3.5" />
        </Link>
        <Link to={`/projects/${p.id}`}>
          <button className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded" data-testid={`project-view-${p.id}`}><Eye className="w-3.5 h-3.5" /></button>
        </Link>
        <button
          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
          onClick={() => onDelete(p.id)}
          data-testid={`project-delete-${p.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </Card>
  );
};

const ListRow = ({ p, onDelete, onStatusChange }) => {
  const hasProof = !!p.latest_proof;
  return (
  <Card data-testid={`project-row-${p.id}`} className={`px-5 py-3.5 hover:shadow-sm transition-all ${STATUS_BG[p.status] || ""}`}>
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <Link to={`/projects/${p.id}`} className="block">
          <p className="text-sm font-semibold text-zinc-950 hover:text-zinc-700 transition-colors truncate">{p.name}</p>
          <p className="text-xs text-zinc-500 mt-0.5 truncate">{p.client_name} · <span className="font-mono text-zinc-400">#{p.job_number}</span></p>
        </Link>
      </div>

      <div className="hidden md:block text-right shrink-0 w-28">
        {p.total_cost > 0 ? (
          <p className="text-sm font-semibold text-zinc-950">{formatGBP(p.total_cost)}</p>
        ) : (
          <p className="text-xs text-zinc-400 italic">—</p>
        )}
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{p.task_count} task{p.task_count !== 1 ? "s" : ""}</p>
      </div>

      <Link
        to={hasProof ? `/projects/${p.id}?proof=latest` : "#"}
        onClick={(e) => { if (!hasProof) e.preventDefault(); }}
        aria-disabled={!hasProof}
        data-testid={`project-quick-proof-${p.id}`}
        title={hasProof ? `Open: ${p.latest_proof.name}` : "No proofs uploaded yet"}
        className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
          hasProof
            ? "bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100"
            : "bg-zinc-50 text-zinc-300 border-zinc-100 cursor-not-allowed"
        }`}
      >
        <FileSearch className="w-3.5 h-3.5" />
        Quick Proof View
      </Link>

      <div className="shrink-0">
        <Select
          value={p.status}
          onChange={(e) => onStatusChange(p.id, e.target.value)}
          className="!py-1.5 !px-2.5 !w-auto text-xs"
          data-testid={`project-row-status-${p.id}`}
        >
          {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </Select>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Link to={`/projects/${p.id}`} data-testid={`project-row-view-${p.id}`}>
          <IconBtn><Eye className="w-4 h-4" /></IconBtn>
        </Link>
        <IconBtn variant="destructive" onClick={() => onDelete(p.id)} data-testid={`project-row-delete-${p.id}`}>
          <Trash2 className="w-4 h-4" />
        </IconBtn>
      </div>
    </div>
  </Card>
  );
};

const ProjectsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("kerboom_projects_view") || "kanban");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get("client") || "";
  const liveFilter = searchParams.get("filter") === "live";
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const clientName = clients.find((c) => c.id === clientFilter)?.name;

  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("client");
    next.delete("filter");
    setSearchParams(next);
  };

  const setViewPersist = (v) => {
    setView(v);
    localStorage.setItem("kerboom_projects_view", v);
  };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/projects");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  // If client is scoped via URL, auto-switch to list view
  useEffect(() => {
    if (clientFilter && view !== "list") setView("list");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilter]);

  const del = async (id) => {
    if (!(await confirm({ title: "Delete this project?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/projects/${id}`);
    await load();
  };

  const changeStatus = async (id, status) => {
    // Optimistic update
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
    await api.patch(`/projects/${id}/status`, { status });
  };

  const filteredKanban = useMemo(() => {
    const needle = q.toLowerCase();
    return items.filter((p) => {
      if (clientFilter && p.client_id !== clientFilter) return false;
      if (liveFilter && !["Not Started", "In Progress", "On Hold"].includes(p.status)) return false;
      return (
        p.name.toLowerCase().includes(needle) ||
        (p.client_name || "").toLowerCase().includes(needle) ||
        String(p.job_number).includes(needle)
      );
    });
  }, [items, q, clientFilter, liveFilter]);

  const filteredList = useMemo(() => {
    return statusFilter === "All"
      ? filteredKanban
      : filteredKanban.filter((p) => p.status === statusFilter);
  }, [filteredKanban, statusFilter]);

  const invoicedCount = items.filter((p) => p.status === "Invoiced").length;

  const byStatus = useMemo(() => {
    const map = { "Not Started": [], "In Progress": [], "Completed": [], "On Hold": [] };
    filteredKanban.forEach((p) => {
      if (map[p.status]) map[p.status].push(p);
    });
    return map;
  }, [filteredKanban]);

  const ViewToggle = (
    <div className="inline-flex items-center border border-zinc-200 rounded-full p-0.5 bg-white">
      <button
        onClick={() => setViewPersist("kanban")}
        data-testid="projects-view-kanban"
        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1.5 ${view === "kanban" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
      >
        <Columns className="w-3.5 h-3.5" />Kanban
      </button>
      <button
        onClick={() => setViewPersist("list")}
        data-testid="projects-view-list"
        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1.5 ${view === "list" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
      >
        <List className="w-3.5 h-3.5" />List
      </button>
    </div>
  );

  return (
    <Shell>
      <PageHeader
        testId="projects-header"
        title="Projects"
        subtitle={
          clientFilter && clientName
            ? `Showing ${liveFilter ? "live " : ""}projects for ${clientName}`
            : "Track every job from brief to invoiced"
        }
        actions={
          <>
            {ViewToggle}
            <Link to="/invoiced" data-testid="invoiced-link">
              <Btn variant="secondary"><PoundSterling className="w-4 h-4" />Invoiced <span className="ml-1 bg-zinc-100 text-zinc-700 text-xs rounded-full px-2 py-0.5">{invoicedCount}</span></Btn>
            </Link>
            <Btn onClick={() => navigate("/projects/new")} data-testid="new-project-btn">
              <Plus className="w-4 h-4" />New Project
            </Btn>
          </>
        }
      />

      {clientFilter && clientName && (
        <div className="mb-4 inline-flex items-center gap-2 bg-zinc-950 text-white rounded-full pl-3 pr-1.5 py-1 text-xs font-medium" data-testid="projects-filter-chip">
          <span>{clientName}{liveFilter ? " · live" : ""}</span>
          <button
            onClick={clearClientFilter}
            className="p-0.5 hover:bg-white/20 rounded-full transition-colors"
            data-testid="projects-filter-clear"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <Input
          data-testid="projects-search"
          placeholder="Search projects, client name or job number..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
        />
        {view === "list" && (
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="!w-auto !py-2.5"
            data-testid="projects-status-filter"
          >
            <option value="All">All statuses</option>
            {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : items.length === 0 ? (
        <Empty
          title="No projects yet"
          description="Start a new project and begin tracking tasks."
          action={<Btn onClick={() => navigate("/projects/new")}><Plus className="w-4 h-4" />New Project</Btn>}
        />
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {KANBAN_COLUMNS.map((status) => (
            <div key={status} data-testid={`column-${status.replace(/\s+/g, "-").toLowerCase()}`} className="flex flex-col">
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
                  <p className="text-sm font-semibold text-zinc-950">{status}</p>
                </div>
                <span className="text-xs text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5">{byStatus[status].length}</span>
              </div>
              <div
                className="space-y-3 overflow-y-auto pr-1 -mr-1"
                style={{ maxHeight: "calc(100vh - 260px)", minHeight: "80px" }}
              >
                {byStatus[status].length === 0 ? (
                  <p className="text-xs text-zinc-400 italic text-center py-4">No projects</p>
                ) : byStatus[status].map((p) => <KanbanCard key={p.id} p={p} onDelete={del} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredList.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-12">No projects match your filters.</p>
          ) : (
            filteredList.map((p) => (
              <ListRow key={p.id} p={p} onDelete={del} onStatusChange={changeStatus} />
            ))
          )}
        </div>
      )}
    </Shell>
  );
};

export default ProjectsList;
